import { stampOccurrenceKeys } from "../lib/forecast/eventIdentity.js";
import { needsDerivedTriggers } from "../lib/forecast/derivedBranch.js";
import type { ForecastEvent } from "../lib/forecast/model.js";
import { derivedTriggersAttached } from "../lib/forecast/reducer.js";
import { reportFailure } from "../lib/failureCounts.js";
import type { CommandDeps } from "./deps.js";

/**
 * Retry waits used while another request holds the generation (HTTP 202). The server bounds one
 * generation at ~3 minutes; these waits cover that and then stop, and the next visit reads
 * whatever the generation left on the row.
 */
export const DERIVED_TRIGGERS_RETRY_MS = [5_000, 10_000, 20_000, 30_000, 30_000, 30_000, 30_000];

/** `failed`: the server answered `!ok` (reported here); `none`: it answered with no events. */
export type DerivedTriggersOutcome =
  | "attached"
  | "unchanged"
  | "none"
  | "failed"
  | "pending"
  | "skipped"
  | "aborted";

export interface DerivedTriggersArgs {
  runId: string;
  signal?: AbortSignal;
  /** Test seam for the retry wait; resolves early when `signal` aborts. */
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
}

function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    }
    signal?.addEventListener("abort", done, { once: true });
  });
}

/**
 * The events come only from the branch row the server read back after its write (the
 * authoritative store). Any failure leaves the branch unchanged. Runs in the background, so a
 * 401 aborts this request rather than redirecting the session.
 */
export async function attachDerivedBranchTriggers(
  deps: CommandDeps,
  { runId, signal, sleep = abortableSleep }: DerivedTriggersArgs,
): Promise<DerivedTriggersOutcome> {
  for (let attempt = 0; ; attempt += 1) {
    if (signal?.aborted) return "aborted";
    // Owner rows only: generation is the branch owner's to ask for.
    const run = deps.store.getState().runsById[runId];
    if (!run || !needsDerivedTriggers(run)) return "skipped";
    const forecastId = run.forecastId as string;

    const unauthorized = new AbortController();
    const requestSignal = signal
      ? AbortSignal.any([signal, unauthorized.signal])
      : unauthorized.signal;
    const response = await deps.services.api.generateDerivedBranchTriggers(forecastId, {
      signal: requestSignal,
      onUnauthorized: () => unauthorized.abort(),
    });
    if (signal?.aborted) return "aborted";

    if (response.pending) {
      if (attempt >= DERIVED_TRIGGERS_RETRY_MS.length) return "pending";
      await sleep(DERIVED_TRIGGERS_RETRY_MS[attempt], signal);
      continue;
    }
    if (!response.ok) {
      // A 401 aborted the request on purpose (the session is gone, not the branch): quiet, like
      // the caller's own abort. Any other `!ok` answer is a lost attach (P-08), logged and
      // counted here where the status is known; the hook's `.catch` only ever sees a throw.
      if (unauthorized.signal.aborted) return "aborted";
      reportFailure(
        "derived_branch_triggers.attach_failed",
        new Error(`POST /api/forecasts/:id/branch-triggers answered status ${response.status}`),
      );
      return "failed";
    }
    const events = response.row?.events;
    if (!Array.isArray(events) || events.length === 0) return "none";
    const before = deps.store.getState().runsById[runId];
    deps.store.dispatch(
      derivedTriggersAttached(runId, forecastId, stampOccurrenceKeys(events as ForecastEvent[])),
    );
    // The reducer no-ops when the run gained events or was replaced while this request was out.
    return deps.store.getState().runsById[runId] === before ? "unchanged" : "attached";
  }
}
